import { useAppDispatch, useAppState } from '@nutastic/nutastic-react-app';
import { CONSTANTS } from '../utils/constants';
import { recentContentUpdated } from '../state/actions.mjs';

const MAX_RECENT = 33;
/**
 * @typedef NuStationDataHookValues
 * @property {function(slug:string)} getMusicProjectIdBySlug - get the ID of a music project
 * @property {function(slug:string)} getVideoIdBySlug - get the ID of video by slug
 * @property {function(slug:string)} getArtistIdBySlug - get the ID of artist by slug
 * @property {function(slug:string)} getArtistBySlug - get the artist by slug
 * @property {function(slug:string)} getVideoBySlug - get video by slug
 * @property {function(id:any, data:any, saveToDb: boolean)} saveRecentMusic - save recent music
 * @property {function(id:any)} getRecentMusicData - get a recent music info
 * @property {function(id:any, data:any, saveToDb: boolean)} saveRecentVideo - save recent video
 * @property {function(id:any)} getRecentVideoData - get a recent video info
 * @property {function} getRecentMusic - get all recent music
 * @property {function} getRecentVideos - get all recent videos
 * @property musicLibrary - available music collection
 * @property artistLibrary - list of creative artists
 * @property videoLibrary - available video collection
 */
/**
 *
 * @returns {NuStationDataHookValues}
 */
const useNuStationData = () => {
  const appDispatch = useAppDispatch();
  const { nuData = {}, content, authState, recentVideos, recentMusic } = useAppState();
  const { isAuthenticated } = authState;
  const { slugMap = {} } = nuData;
  const { videos, artists, musicCollection } = content;

  const getMusicProjectIdBySlug = slug => {
    if (slugMap.hasOwnProperty(CONSTANTS.APPKEY_MUSIC)) {
      return slugMap[CONSTANTS.APPKEY_MUSIC][slug];
    }
    return null;
  }

  const getVideoIdBySlug = slug => {
    if (slugMap.hasOwnProperty(CONSTANTS.APPKEY_VIDEOS)) {
      return slugMap[CONSTANTS.APPKEY_VIDEOS][slug];
    }
    return null;
  }

  const getVideoBySlug = slug => {
    return (videos || []).find(v => v.hasOwnProperty('slug') && v.slug === slug);
  };

  const getArtistIdBySlug = slug => {
    if (slugMap.hasOwnProperty(CONSTANTS.APPKEY_ARTIST)) {
      return slugMap[CONSTANTS.APPKEY_ARTIST][slug];
    }
    return null;
  }

  const getArtistBySlug = slug => {
    return (artists || []).find(a => a.hasOwnProperty('slug') && a.slug === slug);
  };

  const saveRecentMusic = (id, track, saveToDb) => {
    const recentMusicUpdate = (recentMusic || []).filter(rc => rc?.id !== id);
    recentMusicUpdate.unshift({id, data: { track, date: new Date().toLocaleDateString('en-CA')}});
    if (recentMusicUpdate.length > MAX_RECENT) recentMusicUpdate.length = MAX_RECENT;
    appDispatch(recentContentUpdated({
      type: CONSTANTS.APPKEY_RECENT_MUSIC,
      updatedData: recentMusicUpdate,
      saveToDb: isAuthenticated && saveToDb
    }));
  }

  const getRecentMusicData = id => {
    const matchedRecentMusic = (recentMusic || []).find(rc => rc?.id === id);
    return matchedRecentMusic ? matchedRecentMusic?.data : null;
  };

  const getRecentMusic = () => {
    return recentMusic;
  };

  const getRecentVideos = () => {
    return recentVideos;
  };

  const saveRecentVideo = (id, data, saveToDb) => {
    const recentVideosUpdate = (recentVideos || []).filter(rv => rv?.id !== id);
    recentVideosUpdate.unshift({id, data});
    if (recentVideosUpdate.length > MAX_RECENT) recentVideosUpdate.length = MAX_RECENT;
    appDispatch(recentContentUpdated({
      type: CONSTANTS.APPKEY_RECENT_VIDEOS,
      updatedData: recentVideosUpdate,
      saveToDb: isAuthenticated && saveToDb
    }));
  };

  const getRecentVideoData = id => {
    const matchedRecentVideo = (recentVideos || []).find(rv => rv?.id === id);
    return matchedRecentVideo ? matchedRecentVideo?.data : null;
  };

  return {
    getRecentMusic,
    getRecentVideos,
    saveRecentMusic,
    getRecentMusicData,
    saveRecentVideo,
    getRecentVideoData,
    getMusicProjectIdBySlug,
    getVideoIdBySlug,
    getArtistIdBySlug,
    getArtistBySlug,
    getVideoBySlug,
    artistLibrary: artists,
    musicLibrary: musicCollection,
    videoLibrary: videos
  };
};

export {
  useNuStationData
};
